import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import { formatDate } from '@/components/logics/dateUtil'
import { useUserStore } from '@/stores/userStore'

export const useElectronicApplicationStore = defineStore('electronicApplication', {
  state: () => ({
    // 電子申請リスト
    applications: [],

    // テーブル表示用リスト定義
    tableList: [],

    // 編集用フォームデータ
    formData: null,

    // 削除判定フラグ
    isDeletedCompany: false,
    isDeletedBranch: false,
    isDeletedStaff: false,

    // フォームの初期値
    formInit: {
      id: null,
      contractId: null,
      status: 'pending',
      healthInsurance: '',
      companyId: null,
      branchId: null,
      staffId: null,
      content: '',
      memo: '',
      applicableDate: null,
      dateJson: {
        employmentInsuranceMain: {
          date: null,
          userId: null,
          userName: '',
        },
        employmentInsuranceSub: {
          date: null,
          userId: null,
          userName: '',
        },
        publicDocumentSend: {
          date: null,
          userId: null,
          userName: '',
        },
        healthInsuranceDateMain: {
          date: null,
          userId: null,
          userName: '',
        },
        healthInsuranceDateSub: {
          date: null,
          userId: null,
          userName: '',
        },
        pensionMain: {
          date: null,
          userId: null,
          userName: '',
        },
        pensionSub: {
          date: null,
          userId: null,
          userName: '',
        },
        dependentSpouseMain: {
          date: null,
          userId: null,
          userName: '',
        },
        dependentSpouseSub: {
          date: null,
          userId: null,
          userName: '',
        },
        residentTax: {
          date: null,
          userId: null,
          userName: '',
        },
        withholdingTaxSend: {
          date: null,
          userId: null,
          userName: '',
        },
        qualificationCertificate: {
          date: null,
          userId: null,
          userName: '',
        },
        publicNotification: {
          date: null,
          userId: null,
          userName: '',
        },
      },
      colorInfo: {
        healthInsurance: '',
        companyId: '',
        branchId: '',
        staffId: '',
        content: '',
        memo: '',
        applicableDate: '',
        employmentInsuranceMain: '',
        employmentInsuranceSub: '',
        publicDocumentSend: '',
        healthInsuranceDateMain: '',
        healthInsuranceDateSub: '',
        pensionMain: '',
        pensionSub: '',
        dependentSpouseMain: '',
        dependentSpouseSub: '',
        residentTax: '',
        withholdingTaxSend: '',
        qualificationCertificate: '',
        publicNotification: '',
      },
    },

    // 軽量な選択肢データ（TODOストアと統一）
    companyNames: [],
    branchNames: [],
    staffNames: [],

    // 設定情報
    config: {},

    // ステータスマッピング定数を追加
    STATUS_MAP: {
      pending: '申請中',
      unprocessed: '未対応',
      checking: '確認中',
      completed: '完了',
      other: 'その他',
    },

    // 日付グループのマッピング（チェックボックスラベルと対応する日付フィールド）
    DATE_GROUPS: {
      // 単独の日付項目
      publicDocumentSend: ['publicDocumentSend'],
      residentTax: ['residentTax'],
      withholdingTaxSend: ['withholdingTaxSend'],
      qualificationCertificate: ['qualificationCertificate'],
      publicNotification: ['publicNotification'],

      // 複数日付を持つグループ
      employmentInsurance: ['employmentInsuranceMain', 'employmentInsuranceSub'],
      healthInsurance: ['healthInsuranceDateMain', 'healthInsuranceDateSub'],
      pension: ['pensionMain', 'pensionSub'],
      dependentSpouse: ['dependentSpouseMain', 'dependentSpouseSub'],
    },

    // 日付項目のチェックボックス状態（日付JSONとは別に管理）
    dateCheckStatus: {
      employmentInsurance: false,
      publicDocumentSend: false,
      healthInsurance: false,
      pension: false,
      dependentSpouse: false,
      residentTax: false,
      withholdingTaxSend: false,
      qualificationCertificate: false,
      publicNotification: false,
    },
  }),

  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        { label: 'No', key: 'rowNumber', data: (item, value) => value },
        {
          label: '健保',
          key: 'healthInsurance',
          data: item => item.healthInsurance || '-',
        },
        {
          label: '会社',
          key: 'companyId',
          data: (item, value) => item.companyName || '削除された会社',
        },
        {
          label: '事業場',
          key: 'branchId',
          data: (item, value) => item.branchName || '削除された事業場',
        },
        {
          label: '従業員',
          key: 'staffId',
          data: (item, value) => item.staffName || '削除された従業員',
        },
        {
          label: '状況',
          key: 'status',
          data: item => this.getStatusLabel(item.status),
          tag: {
            未対応: 'unattended',
            申請中: 'application-in-progress',
            確認中: 'under-review',
            完了: 'completed',
            その他: 'other',
          },
        },
        {
          label: '該当日',
          key: 'applicableDate',
          data: item => (item.applicableDate ? formatDate(item.applicableDate, '-') : '-'),
        },
        {
          label: '内容',
          key: 'content',
          data: item => item.content || '-',
        },
        {
          label: '雇用保険（作業）',
          key: 'employmentInsuranceMain',
          data: item => this.getDateFromJson(item, 'employmentInsuranceMain'),
        },
        {
          label: '雇用保険（受理）',
          key: 'employmentInsuranceSub',
          data: item => this.getDateFromJson(item, 'employmentInsuranceSub'),
        },
        {
          label: '公文書発送日（本人宛）',
          key: 'publicDocumentSend',
          data: item => this.getDateFromJson(item, 'publicDocumentSend'),
        },
        {
          label: '健康保険（作業）',
          key: 'healthInsuranceDateMain',
          data: item => this.getDateFromJson(item, 'healthInsuranceDateMain'),
        },
        {
          label: '健康保険（受理）',
          key: 'healthInsuranceDateSub',
          data: item => this.getDateFromJson(item, 'healthInsuranceDateSub'),
        },
        {
          label: '厚生年金（作業）',
          key: 'pensionMain',
          data: item => this.getDateFromJson(item, 'pensionMain'),
        },
        {
          label: '厚生年金（受理）',
          key: 'pensionSub',
          data: item => this.getDateFromJson(item, 'pensionSub'),
        },
        {
          label: '第三号（作業）',
          key: 'dependentSpouseMain',
          data: item => this.getDateFromJson(item, 'dependentSpouseMain'),
        },
        {
          label: '第三号（受理）',
          key: 'dependentSpouseSub',
          data: item => this.getDateFromJson(item, 'dependentSpouseSub'),
        },
        {
          label: '住民税異動届出書',
          key: 'residentTax',
          data: item => this.getDateFromJson(item, 'residentTax'),
        },
        {
          label: '源泉徴収票送付確認',
          key: 'withholdingTaxSend',
          data: item => this.getDateFromJson(item, 'withholdingTaxSend'),
        },
        {
          label: '資格証明書',
          key: 'qualificationCertificate',
          data: item => this.getDateFromJson(item, 'qualificationCertificate'),
        },
        {
          label: '公文書通知（会社宛）',
          key: 'publicNotification',
          data: item => this.getDateFromJson(item, 'publicNotification'),
        },
        {
          label: '備考',
          key: 'memo',
          data: item => item.memo || '-',
        },
      ]
    },

    // JSONから日付を取得するヘルパー
    getDateFromJson(item, type) {
      if (!item.dateJson || !item.dateJson[type]) return '-'
      const dateValue = item.dateJson[type].date
      return dateValue ? formatDate(dateValue, '-') : '-'
    },

    // 電子申請一覧取得API呼び出し
    async getApplications() {
      try {
        const response = await apiService.get('api/electronic-applications')

        this.applications = response.applications || []

        // JSONを整形
        this.applications = this.applications.map(item => this.parseJsonFields(item))

        return true
      } catch (error) {
        return false
      }
    },

    // 電子申請詳細取得API呼び出し
    async getApplicationDetail(id) {
      if (!id) return null
      try {
        const response = await apiService.get(`/api/electronic-applications/${id}`)
        if (response.application) {
          // JSON変換を行ってからディープコピーしてフォームデータに設定
          const parsedApplication = this.parseJsonFields(response.application)
          this.formData = JSON.parse(JSON.stringify(parsedApplication))
          return this.formData
        }
        return null
      } catch (error) {
        return null
      }
    },

    // 会社名リストを取得（TODOストアと統一）
    async getCompanyNames() {
      try {
        const response = await apiService.get('/api/companies/names')
        this.companyNames = response.companies || []
        return this.companyNames
      } catch (error) {
        this.companyNames = []
        return []
      }
    },

    // 会社名+健保情報リストを取得（電子申請専用）
    async getCompanyNamesWithHealthInsurance() {
      try {
        const response = await apiService.get('/api/companies/names-with-health-insurance')
        this.companyNames = response.companies || []
        return this.companyNames
      } catch (error) {
        this.companyNames = []
        return []
      }
    },

    // 会社IDで支店名リストを取得（TODOストアと統一）
    async getBranchNamesByCompanyId(companyId) {
      try {
        const response = await apiService.get(`/api/companies/${companyId}/branch-names`)
        this.branchNames = response.branches || []
        return this.branchNames
      } catch (error) {
        this.branchNames = []
        return []
      }
    },

    // 支店IDでスタッフ名リストを取得（TODOストアと統一）
    async getStaffNamesByBranchId(branchId) {
      try {
        const response = await apiService.get(`/api/branches/${branchId}/staff-names`)
        this.staffNames = response.staff || []
        return this.staffNames
      } catch (error) {
        this.staffNames = []
        return []
      }
    },

    // 編集フォームデータの設定
    async setFormData(item = null) {
      // 削除フラグを初期化
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      if (item) {
        // itemが存在する場合は編集モード　詳細を取得してフォームに設定
        if (!item.id) {
          return
        }

        await this.getApplicationDetail(item.id)

        // 会社リスト取得
        await this.getCompanyNamesWithHealthInsurance()
        // 会社が削除されているか判定
        this.isDeletedCompany = item.companyId === null

        // 事業場リスト取得
        if (this.formData.companyId) {
          await this.getBranchNamesByCompanyId(this.formData.companyId)
          // 事業場が削除されているか判定
          this.isDeletedBranch = item.branchId === null
        } else {
          this.branchNames = []
        }

        // 従業員リスト取得
        if (this.formData.branchId) {
          await this.getStaffNamesByBranchId(this.formData.branchId)
          // 従業員が削除されているか判定
          this.isDeletedStaff = item.staffId === null
        } else {
          this.staffNames = []
        }

        // 日付JSONの整形
        if (this.formData.dateJson) {
          for (const key in this.formData.dateJson) {
            const dateInfo = this.formData.dateJson[key]
            if (dateInfo.date) {
              dateInfo.date = formatDate(dateInfo.date, '-')
            }
          }
        }

        // 該当日の日付フォーマット処理
        if (this.formData.applicableDate) {
          this.formData.applicableDate = formatDate(this.formData.applicableDate, '-')
        }

        // 日付のチェック状態を初期化
        this.initDateCheckStatus()
      } else {
        // 新規作成の場合は初期値を使用
        this.formData = JSON.parse(JSON.stringify(this.formInit))
        this.branchNames = []
        this.staffNames = []
        this.isDeletedCompany = false
        this.isDeletedBranch = false
        this.isDeletedStaff = false

        // 日付チェック状態をすべてfalseに初期化
        Object.keys(this.dateCheckStatus).forEach(key => {
          this.dateCheckStatus[key] = false
        })
      }
    },

    // 日付のチェック状態を初期化（編集モード用）
    initDateCheckStatus() {
      if (!this.formData?.dateJson) return

      // 日付グループごとにチェック状態を設定
      Object.entries(this.DATE_GROUPS).forEach(([groupKey, dateFields]) => {
        // グループ内のいずれかの日付が設定されていれば、チェック状態をtrueに
        const hasDate = dateFields.some(field => {
          return this.formData.dateJson[field] && this.formData.dateJson[field].date !== null && this.formData.dateJson[field].date !== ''
        })
        this.dateCheckStatus[groupKey] = hasDate
      })
    },

    // 会社変更時の処理
    async onCompanyChange() {
      if (!this.formData) return

      // 削除フラグをリセット
      this.isDeletedCompany = false
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 事業場と従業員をリセット
      this.formData.branchId = null
      this.formData.staffId = null

      // 健保情報を自動設定
      if (this.formData.companyId) {
        const selectedCompany = this.companyNames.find(c => c.id == this.formData.companyId)
        if (selectedCompany && selectedCompany.healthInsurance !== undefined) {
          this.formData.healthInsurance = selectedCompany.healthInsurance || ''
        }
      } else {
        // 会社が未選択の場合は健保もクリア
        this.formData.healthInsurance = ''
      }

      // 事業場オプションを取得
      await this.getBranchNamesByCompanyId(this.formData.companyId)
      this.staffNames = []
    },

    // 事業場変更時の処理
    async onBranchChange() {
      if (!this.formData) return

      // 削除フラグをリセット
      this.isDeletedBranch = false
      this.isDeletedStaff = false

      // 従業員をリセット
      this.formData.staffId = null

      // 従業員オプションを取得
      await this.getStaffNamesByBranchId(this.formData.branchId)
    },

    // 従業員変更時の処理
    onStaffChange() {
      // 削除フラグをリセット
      this.isDeletedStaff = false
    },

    // 日付更新時の処理
    updateDateInfo(type, value) {
      if (!this.formData?.dateJson?.[type]) return

      // 日付項目が属するグループを探す
      let groupKey = null

      for (const [key, fields] of Object.entries(this.DATE_GROUPS)) {
        if (fields.includes(type)) {
          groupKey = key
          break
        }
      }

      if (!groupKey) return

      const dateItem = this.formData.dateJson[type]

      // グループがチェックされている場合
      if (this.dateCheckStatus[groupKey]) {
        const userStore = useUserStore()

        if (value) {
          // 日付が入力された場合、ユーザー情報を設定
          dateItem.date = value
          dateItem.userId = userStore.currentUserId
          dateItem.userName = userStore.currentUserShortName
        } else {
          dateItem.date = null
          dateItem.userId = null
          dateItem.userName = ''
        }
      } else {
        // チェックされていない場合は日付とユーザー情報をクリア
        dateItem.date = null
        dateItem.userId = null
        dateItem.userName = ''
      }
    },

    // updateCheckStatus
    updateCheckStatus(groupKey, isChecked) {
      if (!this.formData?.dateJson || !this.DATE_GROUPS[groupKey]) return

      // チェック状態を更新
      this.dateCheckStatus[groupKey] = isChecked

      // ユーザー情報を取得
      const userStore = useUserStore()
      const userId = userStore.currentUserId
      const userName = userStore.currentUserShortName

      // 現在の日付を取得
      const currentDate = new Date().toISOString().split('T')[0] // YYYY-MM-DD形式

      // グループ内のすべての日付項目に対して処理
      const dateFields = this.DATE_GROUPS[groupKey]
      dateFields.forEach(field => {
        if (!this.formData.dateJson[field]) return

        if (isChecked) {
          // // チェックが入った場合、現在の日付とユーザー情報を設定
          // this.formData.dateJson[field].date = currentDate
          // this.formData.dateJson[field].userId = userId
          // this.formData.dateJson[field].userName = userName
        } else {
          // チェックが外れた場合、日付とユーザー情報をクリア
          this.formData.dateJson[field].date = null
          this.formData.dateJson[field].userId = null
          this.formData.dateJson[field].userName = ''
        }
      })
    },

    // 電子申請保存API呼び出し
    async saveApplication() {
      if (!this.formData) return null

      try {
        // ディープコピーを作成
        const saveData = JSON.parse(JSON.stringify(this.formData))

        // JSONフィールドを文字列に変換
        saveData.dateJson = JSON.stringify(saveData.dateJson)
        saveData.colorInfo = JSON.stringify(saveData.colorInfo)

        const response = await apiService.post('/api/electronic-applications', saveData)

        // 一覧を再取得
        await this.getApplications()
        return response.message
      } catch (error) {
        return null
      }
    },

    // 電子申請削除API呼び出し
    async deleteApplication() {
      if (!this.formData || !this.formData.id) return null

      try {
        const response = await apiService.delete(`/api/electronic-applications/${this.formData.id}`)

        // 一覧を再取得
        await this.getApplications()
        return response.message
      } catch (error) {
        return null
      }
    },

    // コピー処理
    copyApplication() {
      if (!this.formData) return false

      // IDをリセットして新規として扱う
      this.formData.id = null

      return true
    },

    // 共通JSON変換処理
    parseJsonFields(item) {
      if (!item) return item

      const parsedItem = { ...item }

      try {
        // dateJsonを文字列からオブジェクトに変換
        if (typeof parsedItem.dateJson === 'string') {
          parsedItem.dateJson = JSON.parse(parsedItem.dateJson)
        }
      } catch (error) {
        parsedItem.dateJson = JSON.parse(JSON.stringify(this.formInit.dateJson))
      }

      try {
        // colorInfoを文字列からオブジェクトに変換
        if (typeof parsedItem.colorInfo === 'string') {
          parsedItem.colorInfo = JSON.parse(parsedItem.colorInfo)
        }
      } catch (error) {
        parsedItem.colorInfo = JSON.parse(JSON.stringify(this.formInit.colorInfo))
      }

      return parsedItem
    },

    // ステータス値から表示ラベルを取得（既存のロジックを修正）
    getStatusLabel(status) {
      return this.STATUS_MAP[status] || status
    },

    // ステータスラベルから値を取得（新規追加）
    getStatusValueFromLabel(label) {
      const entry = Object.entries(this.STATUS_MAP).find(([key, value]) => value === label)
      return entry ? entry[0] : label
    },
  },

  getters: {
    // ステータスオプションを取得
    statusOptions(state) {
      return Object.entries(state.STATUS_MAP).map(([value, label]) => ({
        value,
        label,
      }))
    },

    // 会社名リストからセレクトボックス用の選択肢を生成（TODOストアと統一）
    companyNameOptions(state) {
      return state.companyNames.map(company => ({
        label: company.name,
        value: company.id,
      }))
    },

    // 事業場名リストからセレクトボックス用の選択肢を生成（TODOストアと統一）
    branchNameOptions(state) {
      return state.branchNames.map(branch => ({
        label: branch.name,
        value: branch.id,
      }))
    },

    // スタッフ名リストからセレクトボックス用の選択肢を生成（TODOストアと統一）
    staffNameOptions(state) {
      return state.staffNames.map(staff => ({
        label: staff.name,
        value: staff.id,
      }))
    },

    // 会社セレクトの状態を返す
    getCompanySelectState:
      state =>
      (isEdit = false) => {
        const formData = state.formData
        if (!formData) return null
        const companyId = formData.companyId
        if (isEdit && state.isDeletedCompany) return 'deleted'
        if (companyId === null) return 'select'
        return null
      },

    // 事業場セレクトの状態を返す
    getBranchSelectState:
      state =>
      (isEdit = false) => {
        const formData = state.formData
        if (!formData) return 'select'
        const branchId = formData.branchId
        const companyId = formData.companyId
        if (!companyId) return null
        if (isEdit && state.isDeletedBranch) return 'deleted'
        if (branchId === null && companyId) return 'select'
        return null
      },

    // 従業員セレクトの状態を返す
    getStaffSelectState:
      state =>
      (isEdit = false) => {
        const formData = state.formData
        if (!formData) return 'select'

        const staffId = formData.staffId
        const branchId = formData.branchId

        // 事業場が選択されていない場合、従業員も選択できない
        if (!branchId) {
          return null // disabled状態
        }

        // 編集モードで、削除された従業員フラグがtrueの場合
        if (isEdit && state.isDeletedStaff) {
          return 'deleted'
        }

        // staffIdがnullで事業場が選択されている場合は「選択」状態
        if (staffId === null && branchId) {
          return 'select'
        }

        return null // 正常に選択されている状態
      },

    // チェック情報を取得するゲッター
    getCheckInfo: state => category => {
      if (!state.formData?.dateJson?.[category]?.date) return null
      const userStore = useUserStore()

      const userName = state.formData.dateJson[category].userName || '不明なユーザー'

      // ユーザーストアの省略表示メソッドを使用
      const truncatedName = userStore.getTruncatedUserName(userName, 22)

      return `${truncatedName}`
    },

    // 申請状況別の件数
    statusCounts: state => {
      const counts = {
        total: state.applications.length,
        pending: 0,
        unprocessed: 0,
        checking: 0,
        completed: 0,
        other: 0,
      }

      state.applications.forEach(app => {
        counts[app.status] = (counts[app.status] || 0) + 1
      })

      return counts
    },
  },
})
